/* Presentation shared by native sibling radios and bundle-owned choices.
   Bundle selection remains exclusively owned by its Quick View adapter. */
(() => {
  if (customElements.get('sibling-selector')) return;
  let sequence = 0;
  // Short-lived, bounded prefetches. Cart/checkout still validate current inventory.
  const pages = new Map();
  const cacheLifetime = 15000;
  function productPage(url) {
    let cached = pages.get(url);
    if (cached && Date.now() - cached.started < cacheLifetime) return cached.promise;
    if (pages.size >= 6) pages.delete(pages.keys().next().value);
    const request = new AbortController();
    const timeout = setTimeout(() => request.abort(), 10000);
    cached = { started: Date.now() };
    cached.promise = fetch(url, { signal: request.signal, credentials: 'same-origin' })
      .then(response => { if (!response.ok) throw new Error(); return response.text(); })
      .catch(error => { if (pages.get(url) === cached) pages.delete(url); throw error; })
      .finally(() => clearTimeout(timeout));
    pages.set(url, cached);
    return cached.promise;
  }
  customElements.define('sibling-selector', class extends HTMLElement {
    connectedCallback() {
      this.connectFrame = requestAnimationFrame(() => {
        if (!this.isConnected || this.controller) return;
        this.options = this.querySelector('.sibling-selector__options');
        if (!this.options) return;
        this.items = [...this.options.querySelectorAll('[data-sibling-item]')];
        this.more = this.querySelector('[data-sibling-more]');
        this.current = this.querySelector('[data-sibling-current]');
        this.status = document.createElement('p');
        this.status.className = 'sibling-selector__status';
        this.status.setAttribute('role', 'status'); this.status.hidden = true;
        this.options.after(this.status);
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.mobile = matchMedia('(max-width: 767px)');
        this.expanded = this.dataset.expanded === 'true';
        this.options.id ||= `SiblingOptions-${++sequence}`;
        this.more?.setAttribute('aria-controls', this.options.id);
        this.addEventListener('click', (event) => {
          if (!event.target.closest('[data-sibling-more]')) return;
          event.preventDefault();
          this.expanded = !this.expanded;
          this.update();
        }, { signal });
        this.addEventListener('change', (event) => {
          const input = event.target.closest('input[data-sibling-url]');
          if (!input) return;
          event.stopPropagation();
          this.update();
          this.navigate(input);
        }, { signal });
        this.addEventListener('pointerover', event => this.prefetch(this.inputFor(event.target)), { signal });
        this.addEventListener('pointerdown', event => this.prefetch(this.inputFor(event.target)), { signal, passive: true });
        this.addEventListener('focusin', event => this.prefetch(this.inputFor(event.target)), { signal });
        this.addEventListener('focusin', (event) => this.reveal(event.target.closest('[data-sibling-item]')), { signal });
        this.addEventListener('keydown', (event) => {
          if (!event.target.matches('[data-abb-qv-sibling]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const visible = this.items.filter((item) => !item.hidden);
          const index = visible.indexOf(event.target);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length;
          event.preventDefault(); visible[next]?.focus({ preventScroll: true });
        }, { signal });
        this.mobile.addEventListener('change', () => { this.update(); this.reveal(this.selected()); }, { signal });
        this.resize = new ResizeObserver(() => this.update());
        this.resize.observe(this);
        this.visibility = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.visibility.disconnect();
          // Warm at most the two neighboring choices, only when this picker is visible.
          const inputs = [...this.querySelectorAll('input[data-sibling-url]')];
          const index = inputs.findIndex(input => input.checked);
          [inputs[index + 1], inputs[index - 1] || inputs[index + 2]].filter(Boolean).forEach(input => this.prefetch(input));
        });
        this.visibility.observe(this);
        this.update();
        this.frame = requestAnimationFrame(() => this.reveal(this.selected()));
      });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); cancelAnimationFrame(this.frame); cancelAnimationFrame(this.connectFrame);
      this.visibility?.disconnect(); this.navigationSequence = (this.navigationSequence || 0) + 1;
      this.releasePurchaseLock?.(); this.status?.remove();
    }
    selected() { return this.items?.find((item) => item.matches('[aria-pressed="true"]') || item.querySelector('input:checked')); }
    update() {
      const selected = this.selected();
      if (this.current) this.current.textContent = selected?.dataset.siblingLabel || '';
      const columns = Math.min(6, Math.max(4, Number(this.dataset.columns) || 5));
      const rows = Math.min(3, Math.max(1, Number(this.dataset.rows) || 2));
      this.style.setProperty('--sibling-columns', columns);
      const limit = columns * rows - 1;
      const overflow = this.items.length > columns * rows;
      const visible = new Set(this.items.slice(0, limit));
      // Keep the selected color visible even when it belongs to the folded part.
      if (selected && !visible.has(selected)) { visible.delete(this.items[limit - 1]); visible.add(selected); }
      this.items.forEach((item) => { item.hidden = !this.mobile.matches && overflow && !this.expanded && !visible.has(item); });
      if (this.more) {
        this.more.hidden = this.mobile.matches || !overflow;
        this.more.setAttribute('aria-expanded', String(this.expanded));
        const count = this.items.length - limit;
        this.more.setAttribute('aria-label', this.expanded ? 'Ver menos colores' : `Ver ${count} colores más`);
        this.more.querySelector('[data-sibling-more-count]').textContent = this.expanded ? '' : `+${count}`;
        this.more.querySelector('[data-sibling-more-label]').textContent = this.expanded ? 'Ver menos' : 'Más';
      }
      const size = parseFloat(getComputedStyle(this).getPropertyValue('--sibling-mobile-size')) || 62;
      const gap = parseFloat(getComputedStyle(this.options).columnGap) || 8;
      const width = Math.max(1, this.options.clientWidth - 4);
      const fits = this.items.length * size + Math.max(0, this.items.length - 1) * gap <= width;
      this.toggleAttribute('data-fits', fits);
      // Always leave about 40% of the next tile visible, rather than an accidental
      // sliver (or a perfectly aligned row that looks like the end of the list).
      const full = Math.max(1, Math.ceil((width + gap * 0.4) / (size + gap) - 0.4));
      const rendered = fits ? size : Math.max(1, (width - full * gap) / (full + 0.4));
      this.style.setProperty('--sibling-render-size', `${rendered}px`);
    }
    inputFor(target) {
      const item = target.closest('[data-sibling-item]');
      return item?.querySelector('input[data-sibling-url]');
    }
    navigation(input) {
      const info = this.closest('product-info');
      if (!info || typeof info.handleSwapProduct !== 'function' || this.closest('[data-abb-modal]')) return null;
      const url = new URL(input.dataset.siblingUrl, location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
      const full = info.dataset.updateUrl === 'true';
      if (!full) {
        if (!info.sectionId) return null;
        url.searchParams.set('section_id', info.sectionId);
      }
      for (const key of ['preview_theme_id', 'preview_key', '_fd', 'pb']) {
        const value = new URL(location.href).searchParams.get(key);
        if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
      }
      return { info, full, url: url.href, productUrl: input.dataset.siblingUrl };
    }
    prefetch(input) {
      if (!input || input.checked || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const context = this.navigation(input);
      if (context) productPage(context.url).catch(() => {});
    }
    lockPurchases(info, full) {
      this.releasePurchaseLock?.();
      const roots = [info, ...(full ? [document.getElementById('shopify-section-sticky-atc-bar')] : [])].filter(Boolean);
      const buttons = roots.flatMap(root => [...root.querySelectorAll('[name="add"], [type="submit"]')]).map(button => [button, button.disabled]);
      const payments = roots.flatMap(root => [...root.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout')]).map(node => [node, node.inert]);
      buttons.forEach(([button]) => { button.disabled = true; }); payments.forEach(([node]) => { node.inert = true; });
      const prevent = event => { event.preventDefault(); event.stopImmediatePropagation(); };
      roots.forEach(root => root.addEventListener('submit', prevent, true));
      this.releasePurchaseLock = () => {
        buttons.forEach(([button, disabled]) => { button.disabled = disabled; });
        payments.forEach(([node, inert]) => { node.inert = inert; });
        roots.forEach(root => root.removeEventListener('submit', prevent, true));
        this.releasePurchaseLock = null;
      };
    }
    async navigate(input) {
      const context = this.navigation(input);
      if (!context) { window.location.assign(input.dataset.siblingUrl); return; }
      const { info, full, url, productUrl } = context;
      const version = this.navigationSequence = (this.navigationSequence || 0) + 1;
      info.abortController?.abort();
      this.lockPurchases(info, full);
      this.toggleAttribute('data-loading', true); this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Cambiando color…'; this.status.hidden = false;
      try {
        const html = new DOMParser().parseFromString(await productPage(url), 'text/html');
        if (!this.isConnected || version !== this.navigationSequence) return;
        const next = html.querySelector(full ? "product-info[id^='MainProduct']" : 'product-info');
        if (!next || next.dataset.productId !== input.value || (full && (!html.querySelector('main')?.contains(next) || !html.querySelector('title')))) throw new Error();
        if (full && document.getElementById('shopify-section-sticky-atc-bar') && !html.getElementById('shopify-section-sticky-atc-bar')) throw new Error();
        const top = this.getBoundingClientRect().top, scroll = this.options.scrollLeft;
        const modal = this.closest('quick-view-modal');
        const nextSelector = next.querySelector('sibling-selector');
        if (nextSelector && this.expanded) nextSelector.dataset.expanded = 'true';
        this.releasePurchaseLock?.();
        this.removeAttribute('data-loading'); this.removeAttribute('aria-busy'); this.status.hidden = true;
        // Hyper replaces the complete product UI (forms, prices, galleries and apps).
        // Replacing only a photo would leave the previous product purchasable.
        info.handleSwapProduct(productUrl, full, info.dataset.viewMode || 'main-product')(html);
        if (modal) modal.dataset.productUrl = productUrl;
        const scope = full ? document.querySelector('main') : modal || info.parentElement;
        const active = [...scope.querySelectorAll('product-info')].find(node => node !== info && node.dataset.productId === input.value);
        const selector = active?.querySelector('sibling-selector');
        if (selector) requestAnimationFrame(() => {
          const row = selector.querySelector('.sibling-selector__options');
          if (row) row.scrollLeft = scroll;
          selector.querySelector('input:checked')?.focus({ preventScroll: true });
          if (full) window.scrollBy({ top: selector.getBoundingClientRect().top - top, behavior: 'instant' });
          selector.reveal?.(selector.selected?.());
        });
        if (full) {
          const canonical = html.querySelector('link[rel="canonical"]')?.href;
          if (canonical) document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
        }
        document.dispatchEvent(new CustomEvent(modal ? 'quick-view:loaded' : 'sibling:changed', { detail: { productUrl, productId: input.value } }));
      } catch (_) {
        if (!this.isConnected || version !== this.navigationSequence) return;
        pages.delete(url);
        this.querySelectorAll('input[data-sibling-url]').forEach(option => { option.checked = option.value === info.dataset.productId; });
        this.update(); this.status.textContent = 'No se pudo cargar este color. Intenta de nuevo.'; this.status.hidden = false;
      } finally {
        if (version === this.navigationSequence) {
          this.releasePurchaseLock?.(); this.removeAttribute('data-loading'); this.removeAttribute('aria-busy');
        }
      }
    }
    reveal(item) {
      if (!item || !this.mobile?.matches) return;
      const row = this.options.getBoundingClientRect(), box = item.getBoundingClientRect();
      if (box.left < row.left) this.options.scrollLeft -= row.left - box.left + 2;
      else if (box.right > row.right) this.options.scrollLeft += box.right - row.right + 2;
    }
  });
})();
